/**
 * Jovii — THE LINK PICKER, minus the pixels.
 *
 * A title resolves to 50–120 candidate links. The aggregator's "cached" flag lies
 * (of nine marked Ready, three were real), so the only truth is a 16 KB header
 * probe: ~1 s, exact codecs, and a definite verdict on whether the bytes are a
 * film or a `failed_opening_v2.mp4` decoy.
 *
 * The whole design goal is that the user never sees this. Auto-select walks the
 * server's ranking, verifies silently, falls through on failure, and commits the
 * first candidate that is genuinely playable. The sheet only opens when the user
 * asks for it or when everything failed.
 *
 * Owned by the CATALOGUE UI agent. Probing is done through the MEDIA ENGINE's
 * public `probeSource`, imported dynamically so browsing never pays for it.
 */

import type { Candidate, ContentRef, ProbeFailure, ProbedSource } from '$types';
import * as api from './api';
import { failureText } from './format';
import { prefs } from './prefs.svelte';
import { deadSizeVerdict, totalFromContentRange } from './release';
import { rankCandidates, type RankedCandidate } from './rank';
import { qualityAvailability, tierProbeCandidates, preferredAudioAvailability, probedQuality, qualityCandidates, type SelectedQuality } from './quality';

/* ────────────────────────────────────────────────────────────────────────────
 * Tuning
 * ──────────────────────────────────────────────────────────────────────────── */

/** Probes in flight during the silent pass. Each one is a range read via our proxy. */
const CONCURRENCY = 3;
/** A cold link costs ~2.1 s of redirect following before the first byte. */
const PROBE_TIMEOUT_MS = 9000;
/**
 * How long a verified candidate waits for a better-ranked one that is still
 * probing. Without this, one slow link at rank 1 delays everything by its whole
 * timeout; with it, ranking still wins whenever it is close.
 */
const RANK_GRACE_MS = 2200;
/** RD links expire and probes go stale. Ten minutes is well inside the token TTL. */
const PROBE_CACHE_MS = 10 * 60 * 1000;

/* ────────────────────────────────────────────────────────────────────────────
 * Probe results
 * ──────────────────────────────────────────────────────────────────────────── */

export type ProbeStatus =
  | { state: 'unknown' }
  | { state: 'probing' }
  | { state: 'good'; probe: ProbedSource; at: number }
  | { state: 'bad'; failure: ProbeFailure; message: string; at: number };

const UNKNOWN: ProbeStatus = { state: 'unknown' };

/**
 * Verdicts survive navigation, so re-opening a title shows the ticks it already
 * earned instead of re-probing Real-Debrid.
 */
const verdicts = new Map<string, ProbeStatus>();

function cached(candidateId: string): ProbeStatus {
  const hit = verdicts.get(candidateId);
  if (!hit || hit.state === 'unknown' || hit.state === 'probing') return UNKNOWN;
  if (Date.now() - hit.at > PROBE_CACHE_MS) {
    verdicts.delete(candidateId);
    return UNKNOWN;
  }
  return hit;
}

let mediaModule: typeof import('$lib/media') | null = null;
async function media(): Promise<typeof import('$lib/media')> {
  mediaModule ??= await import('$lib/media');
  return mediaModule;
}

/**
 * Verify one candidate. This is the single place a probe is made, and it is
 * exported because the room path needs it too: when the host pins a source, every
 * member must probe *that* candidate and report back — falling through to a
 * better link would put them on a different timeline.
 */
export async function probeCandidate(
  candidate: Candidate,
  options: { signal?: AbortSignal; timeoutMs?: number; force?: boolean } = {}
): Promise<ProbeStatus> {
  if (!options.force) {
    const hit = cached(candidate.id);
    if (hit.state !== 'unknown') return hit;
  }

  const { probeSource, ProbeError } = await media();
  try {
    const probe = await probeSource({
      token: candidate.url,
      options: {
        candidateId: candidate.id,
        timeoutMs: options.timeoutMs ?? PROBE_TIMEOUT_MS,
        ...(options.signal ? { signal: options.signal } : {})
      }
    });

    const status: ProbeStatus = probe.verified
      ? { state: 'good', probe, at: Date.now() }
      : {
          state: 'bad',
          failure: probe.failure ?? 'not-media',
          message: failureText(probe.failure),
          at: Date.now()
        };
    verdicts.set(candidate.id, status);
    return status;
  } catch (err) {
    if ((err as Error)?.name === 'AbortError') return UNKNOWN;
    const failure: ProbeFailure = err instanceof ProbeError ? err.failure : 'not-media';
    if (failure === 'aborted') return UNKNOWN;
    const status: ProbeStatus = {
      state: 'bad',
      failure,
      message: failureText(failure),
      at: Date.now()
    };
    // A 410 means the play token expired: do not remember that as a bad file.
    if (failure !== 'http-error') verdicts.set(candidate.id, status);
    return status;
  }
}

/**
 * The verified probe behind a green tick, or null (docs/STREAMS-AUDIT.md C2d).
 *
 * The verdict cache survives navigation precisely so a tick earned on the title
 * sheet does not have to be re-earned — but until now only the SHEET read it back;
 * the player's controller re-probed the same links seconds later. This is the
 * player's door into the same cache: a Play press that follows a verified tick
 * skips the 16 KB re-read (1.3 s warm, up to 8.7 s on a first touch). Freshness is
 * `cached()`'s: entries expire after PROBE_CACHE_MS, well inside the token TTL.
 */
export function verifiedProbe(candidateId: string): ProbedSource | null {
  const hit = cached(candidateId);
  return hit.state === 'good' ? hit.probe : null;
}

/** Forget every verdict for a fresh set of links (after `resolve({refresh:true})`). */
export function forgetVerdicts(candidates: Candidate[]): void {
  for (const c of candidates) {
    verdicts.delete(c.id);
    screenedOk.delete(c.id);
  }
}

/* ────────────────────────────────────────────────────────────────────────────
 * The bytes=0-0 screen — dead placeholders condemned before anyone probes them
 *
 * 46 of 80 sampled candidates were dead-link placeholders, and there are only four
 * distinct placeholder objects, all under 3 MB (docs/OPEN-DEFECTS.md I12). One
 * one-byte range request answers with the file's REAL total size in Content-Range;
 * `deadSizeVerdict` (release.ts, tested) is the whole judgement. A dead verdict is
 * written into the same cache the probes use, so the silent pass, `playOrder` and
 * every row skip it exactly as if a probe had failed — for a fraction of the bytes.
 * ──────────────────────────────────────────────────────────────────────────── */

/** candidateId → measured total size for candidates that screened PLAUSIBLE. The
 *  real per-file size — for a season pack this is the episode file's, which the
 *  advertised `sizeBytes` is not. */
const screenedOk = new Map<string, { sizeBytes: number; at: number }>();

/** The measured total size of a candidate's actual file, if anything has read one. */
export function measuredSizeBytes(candidateId: string): number | null {
  const screened = screenedOk.get(candidateId);
  if (screened && Date.now() - screened.at <= PROBE_CACHE_MS) return screened.sizeBytes;
  const verdict = cached(candidateId);
  if (verdict.state === 'good' && verdict.probe.sizeBytes > 0) return verdict.probe.sizeBytes;
  return null;
}

/**
 * Screen ONE candidate with a `bytes=0-0` request. Returns the status it settled
 * on: a conclusive `bad` for a placeholder, `unknown` when the answer was not
 * range-shaped (a probe will judge it instead). Never throws.
 */
export async function screenCandidate(c: Candidate, signal?: AbortSignal): Promise<ProbeStatus> {
  const known = cached(c.id);
  if (known.state !== 'unknown') return known;
  const ok = screenedOk.get(c.id);
  if (ok && Date.now() - ok.at <= PROBE_CACHE_MS) return UNKNOWN;

  let res: Response;
  try {
    res = await fetch(c.url, {
      headers: { Range: 'bytes=0-0' },
      credentials: 'same-origin',
      ...(signal ? { signal } : {})
    });
  } catch {
    return UNKNOWN; // network trouble is not a verdict on the link
  }
  try {
    void res.body?.cancel();
  } catch {
    /* the byte is one byte */
  }
  // Anything but a clean 206 is left for the real probe: a 410 is an expired play
  // token, a 200 is a server ignoring Range — neither condemns the file.
  if (res.status !== 206) return UNKNOWN;
  const total = totalFromContentRange(res.headers.get('Content-Range'));
  if (total === null) return UNKNOWN;

  if (deadSizeVerdict(total) === 'dead') {
    const status: ProbeStatus = {
      state: 'bad',
      failure: 'placeholder',
      message: failureText('placeholder'),
      at: Date.now()
    };
    verdicts.set(c.id, status);
    return status;
  }
  screenedOk.set(c.id, { sizeBytes: total, at: Date.now() });
  return UNKNOWN;
}

/**
 * Screen a list in order, a few at a time. `onVerdict` fires for every candidate
 * that settles (dead OR measured-plausible), so a sheet can repaint as the list
 * cleans itself. Abort through the signal; already-settled candidates are free.
 */
export async function screenCandidates(
  list: readonly Candidate[],
  options: { signal?: AbortSignal; concurrency?: number; onVerdict?: (c: Candidate, status: ProbeStatus) => void } = {}
): Promise<void> {
  const width = options.concurrency ?? 3;
  let index = 0;
  const workers = Array.from({ length: Math.min(width, list.length) }, async () => {
    while (index < list.length && !options.signal?.aborted) {
      const c = list[index++]!;
      const before = cached(c.id);
      if (before.state !== 'unknown') continue;
      const status = await screenCandidate(c, options.signal);
      if (options.signal?.aborted) return;
      if (status.state !== 'unknown' || screenedOk.has(c.id)) options.onVerdict?.(c, status);
    }
  });
  await Promise.all(workers);
}

/* ────────────────────────────────────────────────────────────────────────────
 * Pinning
 *
 * A link chosen by hand has to survive the navigation into the player, which is
 * a different component tree. The pin is per content ref and lives for the tab:
 * it is a choice, not a preference, and it should not outlive the session.
 * ──────────────────────────────────────────────────────────────────────────── */

const pins = new Map<string, Candidate>();

export function contentKey(ref: Pick<ContentRef, 'kind' | 'imdbId' | 'season' | 'episode'>): string {
  return `${ref.kind}:${ref.imdbId}:${ref.season ?? ''}:${ref.episode ?? ''}`;
}

export function pinSource(ref: ContentRef, candidate: Candidate): void {
  pins.set(contentKey(ref), candidate);
}

export function pinnedSource(ref: ContentRef): Candidate | undefined {
  return pins.get(contentKey(ref));
}

export function unpinSource(ref: ContentRef): void {
  pins.delete(contentKey(ref));
}

/**
 * Candidates ordered the way the player should try them: the hand-picked link
 * first, then anything already verified, then this user's ranking.
 */
export function playOrder(ref: ContentRef, candidates: Candidate[]): Candidate[] {
  const pinned = pinnedSource(ref);
  const ranked = qualityCandidates(candidates, { maxQuality: prefs.maxQuality, audioLang: prefs.audioLang });
  const scored = ranked.map((candidate, index) => {
    if (pinned && candidate.id === pinned.id) return { candidate, key: -2, index };
    const verdict = cached(candidate.id);
    if (verdict.state === 'good') return { candidate, key: -1, index };
    if (verdict.state === 'bad') return { candidate, key: 1, index };
    return { candidate, key: 0, index };
  });
  scored.sort((a, b) => a.key - b.key || a.index - b.index);
  const ordered = scored.map((x) => x.candidate);
  // Keep other tiers available for a later explicit quality choice.
  return [...ordered, ...candidates.filter(c => !ordered.some(o => o.id === c.id))];
}

/* ────────────────────────────────────────────────────────────────────────────
 * Ranking
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Order the rows for THIS person and THIS browser.
 *
 * Delegates to `rank.ts`, which is the one scoring function in the client and the one
 * that can explain itself — the sheet gets `rankedForUser` below when it wants the
 * reasons, and this stays for every caller that only wants an order.
 *
 * What changed when it moved there (docs/LINKS-AUDIT.md): the old version knew only
 * about the quality ceiling and the language, so it could not see that 40.8 % of the
 * rows above the fold are decoys, that a not-cached flag is a 98 %-accurate death
 * sentence, or that the HEVC row it was quietly ranking down is the one this Mac
 * decodes in hardware.
 */
export function rankForUser(candidates: Candidate[]): Candidate[] {
  return rankedForUser(candidates).map((r) => r.candidate);
}

/** The same ordering, with the score and the reasons attached. */
export function rankedForUser(candidates: Candidate[]): RankedCandidate[] {
  return rankCandidates(candidates, {
    maxQuality: prefs.maxQuality,
    audioLang: prefs.audioLang
  });
}

/* ────────────────────────────────────────────────────────────────────────────
 * The selector
 * ──────────────────────────────────────────────────────────────────────────── */

export type SelectorPhase =
  | 'idle'
  | 'resolving' // asking AIOStreams through our service
  | 'verifying' // silent header probes, falling through
  | 'ready' // a verified source is committed
  | 'empty' // nobody has this title
  | 'failed'; // candidates exist, none of them opened

export interface SelectedLink {
  candidate: Candidate;
  probe: ProbedSource;
}

export interface ResolveMeta {
  count: number;
  from: 'fresh' | 'cache' | 'stale';
  ageMs: number;
  upstreamMs: number;
  raw: number;
}

/**
 * One title's worth of link finding. Create it when a watch screen opens, call
 * `start()`, and read `phase` / `selected`. Call `dispose()` on unmount.
 */
export class SourceSelector {
  readonly ref: ContentRef;

  phase = $state<SelectorPhase>('idle');
  candidates = $state<Candidate[]>([]);
  /** candidateId → verdict. Written by both the silent pass and explicit picks. */
  statuses = $state<Record<string, ProbeStatus>>({});
  selected = $state<SelectedLink | null>(null);
  error = $state('');
  meta = $state<ResolveMeta | null>(null);
  /** How many candidates the silent pass has finished. Drives the progress line. */
  attempted = $state(0);
  /** True while an explicit (user-initiated) probe is running. */
  choosing = $state<string | null>(null);

  #abort: AbortController | null = null;
  #choice: AbortController | null = null;
  #batches: AbortController[] = [];
  #batchPromise: Promise<void> | null = null;
  #batchCandidates: Candidate[] = [];
  qualityChecking = $state(false);
  #graceTimer: ReturnType<typeof setTimeout> | null = null;
  #onSelect: ((link: SelectedLink) => void) | undefined;
  readonly #probe: typeof probeCandidate;

  constructor(ref: ContentRef, options: { onSelect?: (link: SelectedLink) => void; probe?: typeof probeCandidate } = {}) {
    this.ref = ref;
    this.#onSelect = options.onSelect;
    this.#probe = options.probe ?? probeCandidate;
  }

  /** Everything currently known, ordered the way auto-select would try it. */
  get ordered(): Candidate[] {
    return rankForUser(this.candidates);
  }

  statusOf(candidate: Candidate): ProbeStatus {
    return this.statuses[candidate.id] ?? cached(candidate.id);
  }

  #setStatus(candidateId: string, status: ProbeStatus): void {
    this.statuses = { ...this.statuses, [candidateId]: status };
  }

  #commit(candidate: Candidate, probe: ProbedSource): void {
    this.selected = { candidate, probe };
    this.phase = 'ready';
    this.#onSelect?.({ candidate, probe });
  }

  /** Resolve candidates, then verify silently until one plays. */
  async start(options: { refresh?: boolean } = {}): Promise<void> {
    this.dispose();
    const abort = new AbortController();
    this.#abort = abort;

    this.phase = 'resolving';
    this.error = '';
    this.selected = null;
    this.attempted = 0;

    let result: api.ResolveResult;
    try {
      result = await api.resolve(this.ref, { signal: abort.signal, ...(options.refresh ? { refresh: true } : {}) });
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') return;
      this.phase = 'failed';
      this.error =
        err instanceof api.ApiError && err.code === 'aiostreams-not-configured'
          ? 'The server has no link source configured.'
          : ((err as Error).message ?? 'Could not look for links.');
      return;
    }
    if (abort.signal.aborted) return;

    if (options.refresh) forgetVerdicts(result.candidates);

    this.candidates = result.candidates;
    this.meta = {
      count: result.count,
      from: result.from,
      ageMs: result.ageMs,
      upstreamMs: result.upstreamMs,
      raw: result.raw
    };
    // Seed from the cache so a revisit shows its ticks immediately.
    const seeded: Record<string, ProbeStatus> = {};
    for (const c of result.candidates) {
      const hit = cached(c.id);
      if (hit.state !== 'unknown') seeded[c.id] = hit;
    }
    this.statuses = seeded;

    if (result.candidates.length === 0) {
      this.phase = 'empty';
      return;
    }

    await this.#autoVerify(abort);
  }

  /**
   * Same as `start()` but with the candidate list already in hand. The watch
   * screen has usually resolved them a second earlier; asking the service twice
   * for the same 90 links would be wasteful and would show a spinner for nothing.
   */
  async adopt(candidates: Candidate[]): Promise<void> {
    this.dispose();
    const abort = new AbortController();
    this.#abort = abort;

    this.error = '';
    this.selected = null;
    this.attempted = 0;
    this.candidates = candidates;

    const seeded: Record<string, ProbeStatus> = {};
    for (const c of candidates) {
      const hit = cached(c.id);
      if (hit.state !== 'unknown') seeded[c.id] = hit;
    }
    this.statuses = seeded;

    if (candidates.length === 0) {
      this.phase = 'empty';
      return;
    }
    await this.#autoVerify(abort);
  }

  /**
   * The silent pass. Probes in rank order with a little concurrency, and commits
   * the best-ranked verified candidate — waiting a moment for a better-ranked
   * probe still in flight, but never waiting out a whole timeout for one.
   */
  async #autoVerify(abort: AbortController): Promise<void> {
    this.phase = 'verifying';

    // Walk past a tier full of foreign copies to reach lower-quality matching audio.
    const shortlist = tierProbeCandidates(qualityCandidates(this.candidates, { maxQuality: prefs.maxQuality, audioLang: prefs.audioLang }));
    const withinCeiling = (status: ProbeStatus): ProbeStatus => {
      if (status.state !== 'good') return status;
      const audio = preferredAudioAvailability(status.probe, prefs.audioLang);
      if (audio.state !== 'match') return { state: 'bad', failure: 'not-media', message: audio.reason, at: Date.now() };
      const actual = probedQuality(status.probe);
      return actual !== 'unknown' && Number.parseInt(actual) > Number.parseInt(prefs.maxQuality)
        ? { state: 'bad', failure: 'not-media', message: 'Above your selected quality', at: Date.now() }
        : status;
    };
    const verdictOf: ProbeStatus[] = shortlist.map((c) => withinCeiling(this.statusOf(c)));

    // An already-verified candidate from a previous visit short-circuits everything.
    const known = verdictOf.findIndex((v) => v.state === 'good');
    if (known >= 0 && verdictOf.slice(0, known).every((v) => v.state === 'bad')) {
      const status = verdictOf[known]!;
      if (status.state === 'good') {
        this.#commit(shortlist[known]!, status.probe);
        return;
      }
    }

    let committed = false;
    let next = 0;
    let active = 0;

    const settle = (): void => {
      if (committed || abort.signal.aborted) return;

      let firstGood = -1;
      for (let i = 0; i < verdictOf.length; i += 1) {
        if (verdictOf[i]!.state === 'good') {
          firstGood = i;
          break;
        }
      }
      if (firstGood < 0) return;

      const blocked = verdictOf.slice(0, firstGood).some((v) => v.state !== 'bad');
      if (blocked) {
        const good = verdictOf[firstGood]!;
        const waited = good.state === 'good' ? Date.now() - good.at : 0;
        if (waited < RANK_GRACE_MS) {
          // Re-check when the grace expires; a better link may still land first.
          if (this.#graceTimer) clearTimeout(this.#graceTimer);
          this.#graceTimer = setTimeout(settle, RANK_GRACE_MS - waited + 20);
          return;
        }
      }

      const status = verdictOf[firstGood]!;
      if (status.state !== 'good') return;
      committed = true;
      abort.abort(); // stop the probes still in flight; their bytes are wasted now
      this.#commit(shortlist[firstGood]!, status.probe);
    };

    await new Promise<void>((done) => {
      const pump = (): void => {
        if (committed || abort.signal.aborted) {
          if (active === 0) done();
          return;
        }
        while (active < CONCURRENCY && next < shortlist.length) {
          const index = next;
          next += 1;
          const candidate = shortlist[index]!;
          if (verdictOf[index]!.state === 'bad' || verdictOf[index]!.state === 'good') continue;

          active += 1;
          this.#setStatus(candidate.id, { state: 'probing' });
          verdictOf[index] = { state: 'probing' };

          void this.#probe(candidate, { signal: abort.signal })
            .then((status) => {
              verdictOf[index] = withinCeiling(status);
              // Always write it back, even when the pass was aborted mid-probe: an
              // aborted probe resolves to `unknown`, and skipping the write would
              // leave the row spinning for ever on a question nobody is asking.
              this.#setStatus(candidate.id, status);
              if (status.state !== 'unknown') this.attempted += 1;
              settle();
            })
            .finally(() => {
              active -= 1;
              pump();
            });
        }
        if (active === 0 && next >= shortlist.length) done();
      };
      pump();
    });

    if (this.#graceTimer) {
      clearTimeout(this.#graceTimer);
      this.#graceTimer = null;
    }
    if (committed || abort.signal.aborted) return;

    settle();
    if (this.selected === null) {
      this.phase = 'failed';
      this.error = 'No playable copy with verified preferred audio was found. Try refreshing or change your audio preference.';
    }
  }

  /**
   * An explicit pick from the sheet. Verifies the chosen candidate and commits it;
   * on failure it says why and stays where it is — the user asked for THIS one.
   */
  async choose(candidate: Candidate, quality?: SelectedQuality): Promise<boolean> {
    // A background batch check must never make the user wait: choosing is what
    // the sheet is FOR, and the batch is speculative work about rows they have
    // now stopped caring about. Give the choice the probe concurrency.
    this.cancelChecks();
    this.#abort?.abort();
    this.#choice?.abort();
    const choice = new AbortController();
    this.#choice = choice;
    this.choosing = candidate.id;
    this.error = '';
    this.#setStatus(candidate.id, { state: 'probing' });
    try {
      const status = await this.#probe(candidate, { force: true, timeoutMs: 12000, signal: choice.signal });
      if (choice.signal.aborted) return false;
      this.#setStatus(candidate.id, status);
      if (status.state === 'good') {
        const audio = preferredAudioAvailability(status.probe, prefs.audioLang);
        if (audio.state !== 'match') { this.error = audio.reason; return false; }
        if (quality !== undefined && probedQuality(status.probe) !== quality) return false;
        this.#commit(candidate, status.probe);
        return true;
      }
      this.error = status.state === 'bad' ? `${status.message}. Try another link.` : 'That check was cancelled.';
      return false;
    } finally {
      if (this.#choice === choice) this.choosing = null;
    }
  }

  /** Verify ONE candidate without committing — the row's own check button. Unlike
   *  `verify`, it re-probes a row that already failed: the user is asking again. */
  async checkOne(candidate: Candidate): Promise<void> {
    if (this.statusOf(candidate).state === 'probing') return;
    this.#setStatus(candidate.id, { state: 'probing' });
    const status = await this.#probe(candidate, { force: true });
    this.#setStatus(candidate.id, status);
  }

  /** Verify a batch of candidates the user is looking at, without committing. */
  verify(candidates: Candidate[]): Promise<void> {
    if (this.#batchPromise) return this.#batchPromise;
    const probeFor = (c: Candidate) => {
      const status = this.statusOf(c);
      return status.state === 'good' ? status.probe : status.state === 'bad' ? null : undefined;
    };
    const list = tierProbeCandidates(qualityCandidates(candidates,
      { maxQuality: '2160p', audioLang: prefs.audioLang }, undefined, probeFor), probeFor)
      .filter(c => this.statusOf(c).state === 'unknown');
    const abort = new AbortController();
    this.#batches.push(abort);
    this.#batchCandidates = list;
    this.qualityChecking = list.length > 0;
    let index = 0;
    const run = Promise.all(Array.from({ length: Math.min(CONCURRENCY, list.length) }, async () => {
      while (index < list.length && !abort.signal.aborted) {
        const candidate = list[index++]!;
        if (this.statusOf(candidate).state !== 'unknown') continue;
        this.#setStatus(candidate.id, { state: 'probing' });
        const status = await this.#probe(candidate, { signal: abort.signal });
        if (!abort.signal.aborted) this.#setStatus(candidate.id, status);
      }
    })).then(() => {}).finally(() => {
      if (this.#batchPromise === run) {
        this.#batchPromise = null;
        this.#batchCandidates = [];
        this.#batches = [];
        this.qualityChecking = false;
      }
    });
    this.#batchPromise = run;
    return run;
  }

  qualityDisabledReason(quality: SelectedQuality): string | null {
    const result = qualityAvailability(this.candidates, quality, prefs.audioLang, c => {
      const status = this.statusOf(c);
      return status.state === 'good' ? status.probe : status.state === 'bad' ? null : undefined;
    }, { checking: this.qualityChecking });
    return result.disabled ? result.reason : null;
  }

  /**
   * The committed link verified but would not actually play. Blacklist it and
   * resume the silent pass — this is the one case where falling through happens
   * after a commit, and it is why the player reports failures instead of just
   * showing an error.
   */
  async reject(candidateId: string, reason = 'Would not play'): Promise<void> {
    const status: ProbeStatus = { state: 'bad', failure: 'not-media', message: reason, at: Date.now() };
    verdicts.set(candidateId, status);
    this.#setStatus(candidateId, status);
    if (this.selected?.candidate.id === candidateId) this.selected = null;

    this.#abort?.abort();
    const abort = new AbortController();
    this.#abort = abort;
    await this.#autoVerify(abort);
  }

  /** Ask the service for a fresh candidate list (new play tokens, new scrape). */
  async refresh(): Promise<void> {
    await this.start({ refresh: true });
  }

  /** Stop any in-flight "check these rows" batch. Leaves verdicts already in. */
  cancelChecks(): void {
    for (const batch of this.#batches) batch.abort();
    for (const c of this.#batchCandidates) {
      if (this.statusOf(c).state === 'probing') this.#setStatus(c.id, UNKNOWN);
    }
    this.#batches = [];
    this.#batchCandidates = [];
    this.#batchPromise = null;
    this.qualityChecking = false;
  }

  dispose(): void {
    this.#choice?.abort();
    this.#choice = null;
    this.#abort?.abort();
    this.#abort = null;
    this.cancelChecks();
    if (this.#graceTimer) {
      clearTimeout(this.#graceTimer);
      this.#graceTimer = null;
    }
  }
}

/* ────────────────────────────────────────────────────────────────────────────
 * THE PICKER FEED — one picker, two mounts
 *
 * `SourcePicker.svelte` is the ONE link picker; docs/STATUS.md carried "two link
 * pickers still exist" as a known defect until it was collapsed onto this seam.
 * The catalogue mounts it over a `SourceSelector` (below); the player mounts it
 * over its `PlayerController`. Every getter is read inside the component's
 * deriveds, so both stay live without the component knowing which world it is in.
 * ──────────────────────────────────────────────────────────────────────────── */

export interface PickerFeed {
  /** Best-first, as this mount orders them. */
  readonly candidates: Candidate[];
  /** True while the candidate list itself is being fetched. */
  readonly resolving: boolean;
  /** True when the list came from the service's cache rather than a fresh scrape. */
  readonly fromCache: boolean;
  /** A line of trouble to show above the list, or ''. */
  readonly error: string;
  /** The candidate playback is (or will be) on, or null. */
  readonly activeId: string | null;
  /** What the active row's pill says: the player is 'Playing', the catalogue —
   *  which holds playback back while the sheet is up — has only 'Chosen'. */
  readonly activeLabel: string;
  /** The candidate an explicit tap is verifying right now, or null. */
  readonly choosingId: string | null;
  statusOf(candidate: Candidate): ProbeStatus;
  /** Choose = play. Resolves true when the candidate verified and was committed. */
  choose(candidate: Candidate, quality?: SelectedQuality): Promise<boolean>;
  chooseQuality?(quality: SelectedQuality): Promise<boolean>;
  qualityDisabledReason?(quality: SelectedQuality): string | null;
  /** Verify one row without switching to it. */
  check(candidate: Candidate): Promise<void>;
  /** Verify a handful without switching. */
  checkMany(candidates: Candidate[]): Promise<void>;
  /** Ask the service for a fresh list (new tokens, new scrape). */
  refresh(): Promise<void>;
  /** Stop any in-flight batch checks (the sheet is closing). */
  cancelChecks(): void;
}

/** The catalogue's side of the seam. */
export function selectorFeed(selector: SourceSelector): PickerFeed {
  return {
    get candidates() {
      return selector.ordered;
    },
    get resolving() {
      return selector.phase === 'resolving';
    },
    get fromCache() {
      const meta = selector.meta;
      return meta !== null && meta.from !== 'fresh';
    },
    get error() {
      return selector.error;
    },
    get activeId() {
      return selector.selected?.candidate.id ?? null;
    },
    // "Chosen", not "Playing": while this picker is open the player is held back,
    // so the row must not claim playback that has not started.
    activeLabel: 'Chosen',
    get choosingId() {
      return selector.choosing;
    },
    statusOf: (c) => selector.statusOf(c),
    choose: (c, quality) => selector.choose(c, quality),
    check: (c) => selector.checkOne(c),
    checkMany: (cs) => selector.verify(cs),
    qualityDisabledReason: q => selector.qualityDisabledReason(q),
    refresh: () => selector.refresh(),
    cancelChecks: () => selector.cancelChecks()
  };
}
